import * as React from "react";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import makeStyles from "@mui/styles/makeStyles";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import Button from "@mui/material/Button";
import { Link } from "react-router-dom";
import { routes } from "../../Routes";
import { IAppState } from "../../store/Store";
import { TAuthState } from "../../types/Auth";
import { ContentPage, TContentState } from "../../types/Content";
import {
  DEFAULT_PRIORITY_FILTER,
  DEFAULT_STATUS_FILTER,
  TTasksState,
} from "../../types/Tasks";
import { getTasksList } from "../../actions/TaskActions";
import { filterTasks } from "../../helpers/taskFilter";
import TaskListItem from "../tasks/TaskListItem";

const HOME_TASK_LIMIT = 3;

const useStyles = makeStyles({
  root: {
    marginBottom: "15px",
  },
  showMore: {
    marginTop: "5px",
  },
});

const WelcomeSection: React.FC = (): JSX.Element | null => {
  const classes = useStyles({});
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const authState: TAuthState = useSelector<IAppState, TAuthState>(
    (state) => state.authState
  );
  const contentState: TContentState = useSelector<IAppState, TContentState>(
    (state) => state.contentState
  );
  const tasksState: TTasksState = useSelector<IAppState, TTasksState>(
    (state) => state.tasksState
  );

  const user = authState.data?.user;
  const hasTasksPage = !!contentState.customPages?.some(
    (page) => page.type === ContentPage.TASKS
  );
  const showTasks = !!user && hasTasksPage;

  useEffect(() => {
    if (showTasks) {
      dispatch(getTasksList());
    }
  }, [dispatch, showTasks]);

  const tasks = useMemo(
    () =>
      showTasks && tasksState.data
        ? filterTasks(
            tasksState.data,
            "",
            DEFAULT_STATUS_FILTER,
            DEFAULT_PRIORITY_FILTER
          )
        : [],
    [showTasks, tasksState.data]
  );

  if (!user) {
    return null;
  }

  return (
    <div className={classes.root}>
      <Typography variant="h5" component="h2">
        {t("home.welcome", {
          name: user.name,
          interpolation: { escapeValue: false },
        })}
      </Typography>
      {tasks.length > 0 && (
        <List>
          {tasks.slice(0, HOME_TASK_LIMIT).map((task) => (
            <TaskListItem key={task.id} task={task} />
          ))}
        </List>
      )}
      {tasks.length > HOME_TASK_LIMIT && (
        <Button
          fullWidth
          variant="outlined"
          color="primary"
          className={classes.showMore}
          component={Link}
          to={routes.tasks}
        >
          {t("home.show_more")}
        </Button>
      )}
    </div>
  );
};

export default WelcomeSection;
